import { useCountdown } from '@mntn/select-components'
import { cn } from '@mntn/ui-components'
import { useMemo } from 'react'
import { Badge, type Props as BadgeProps } from './badge'
import { isStartingSoon } from './utils'

type TimeComponents = {
  months?: number
  days: number
  hours: number
  minutes: number
  seconds?: number
}

/**
 * Format a countdown label based on time components
 */
export const formatCountdown = (timeComponents: TimeComponents): string => {
  const { months = 0, days, hours, minutes } = timeComponents

  // Check if countdown has expired
  if (days <= 0 && hours <= 0 && minutes <= 0) {
    return 'Expired'
  }

  if (months > 0) return `Expires: ${months}mo`

  if (days >= 7) {
    const weeks = Math.floor(days / 7)
    const remainingDays = days % 7
    const label = remainingDays > 0 ? `${weeks}w ${remainingDays}d` : `${weeks}w`
    return `Expires: ${label}`
  }

  if (days > 0) return `Expires: ${days}d ${hours}h`

  return `Expires: ${hours}h ${minutes}m`
}

type BadgePropsResult = Omit<BadgeProps, 'shape'>

type CountdownToStartVariant = 'light' | 'dark' | 'card'

/**
 * Get badge props for light variant
 */

// biome-ignore lint/plugin: The badge contract preserves closed type and variant literals.
const getLightBadgeProps = (
  label: string,
  isExpired: boolean,
  startsSoon: boolean,
  className?: string,
): BadgePropsResult => {
  if (startsSoon) {
    return {
      label,
      iconProps: {
        name: 'TriangleAlertFilled',
        className: 'size-4 text-error',
      },
      type: 'error',
      variant: 'subtle',
      className: cn('text-error', className),
    }
  }
  if (isExpired) {
    return {
      label: 'Expired',
      type: 'warning',
      variant: 'subtle',
      iconProps: {
        name: 'TriangleAlertFilled',
        className: 'size-4 text-warning',
      },
      className: cn('text-warning', className),
    }
  }
  return {
    label,
    type: 'primary',
    variant: 'solid',
    className: cn('text-secondary-accent-inverse', className),
  }
}

/**
 * Get badge props for card variant (for white background cards)
 */

// biome-ignore lint/plugin: The badge contract preserves closed type and variant literals.
const getCardBadgeProps = (
  label: string,
  isExpired: boolean,
  startsSoon: boolean,
  className?: string,
): BadgePropsResult => {
  if (startsSoon) {
    return {
      label,
      iconProps: {
        name: 'TriangleAlertFilled',
        className: 'size-4 text-error',
      },
      type: 'error',
      variant: 'subtle',
      className: cn('text-error', className),
    }
  }
  if (isExpired) {
    return {
      label: 'Expired',
      type: 'warning',
      variant: 'subtle',
      iconProps: {
        name: 'TriangleAlert',
        className: 'size-4 text-warning',
      },
      className,
    }
  }
  return {
    label,
    iconProps: {
      name: 'CircleAlertFilled',
      className: 'size-4 text-blue-600',
    },
    type: 'primary',
    variant: 'subtle',
    className: cn('text-blue-600', className),
  }
}

/**
 * Get badge props for dark variant
 */

// biome-ignore lint/plugin: The badge contract preserves closed type and variant literals.
const getDarkBadgeProps = (
  label: string,
  isExpired: boolean,
  startsSoon: boolean,
  subtle: boolean,
  className?: string,
): BadgePropsResult => {
  if (startsSoon) {
    return {
      label,
      iconProps: {
        name: 'TriangleAlertFilled',
        className: 'size-4 text-error',
      },
      type: 'error',
      variant: 'dark',
      className: cn('text-error', subtle && 'bg-transparent', className),
    }
  }
  if (isExpired) {
    return {
      label: 'EXPIRED',
      iconProps: {
        name: 'TriangleAlert',
        className: 'size-4 text-warning',
      },
      type: 'secondary',
      variant: 'dark',
      className: cn('text-warning', subtle && 'bg-transparent', className),
    }
  }
  if (subtle) {
    return {
      label,
      type: 'primary',
      variant: 'dark',
      className: cn('text-secondary-accent-inverse bg-transparent', className),
    }
  }
  return {
    label,
    iconProps: {
      name: 'CircleAlertFilled',
      className: 'size-4 text-blue-400',
    },
    type: 'warning',
    variant: 'dark',
    className: cn('text-blue-400', className),
  }
}

/**
 * CountdownToStart component displays a countdown timer badge to a given date
 * - If target date has passed, displays "Expired"
 * - If target date is approaching, shows a dynamic countdown
 * - Updates every second
 */
export const CountdownToStart = ({
  startTime,
  variant = 'light',
  subtle = false,
  className,
}: {
  startTime: Date
  variant?: CountdownToStartVariant
  subtle?: boolean
  className?: string
}) => {
  // Get time left until startTime
  const timeLeft = useCountdown(startTime)

  // Determine if the target date is imminent
  const startsSoon = useMemo(() => isStartingSoon(startTime), [startTime])

  const badgeClassName = useMemo(() => cn('justify-start', subtle ? 'p-0' : 'p-3', className), [className, subtle])

  // Calculate badge properties based on countdown state
  const badgeProps = useMemo<BadgePropsResult>(() => {
    const label = formatCountdown(timeLeft)

    const isExpired = label === 'Expired'

    switch (variant) {
      case 'dark':
        return getDarkBadgeProps(label, isExpired, startsSoon, subtle, badgeClassName)
      case 'card':
        return getCardBadgeProps(label, isExpired, startsSoon, badgeClassName)
      default:
        return getLightBadgeProps(label, isExpired, startsSoon, badgeClassName)
    }
  }, [timeLeft, startsSoon, variant, subtle, badgeClassName])

  return <Badge {...badgeProps} shape="square" />
}
